import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Container, Divider, Stack, TextField, Tooltip, Typography } from '@mui/material';
import EventTopBar from '../components/eventDetails/EventTopBar';
import { paymentOutcomeStyles, paymentStatusStyles, topBarStyles } from '../components/eventDetails/eventDetailsStyles';
import { fetchPaymentStatus, updateAttendeeInfo } from '../services/api';

const getPaymentReturnParams = (location) => {
  const params = new URLSearchParams(location.search);
  const freeParam = String(params.get('free') || '').toLowerCase();
  return {
    purchaseRef: params.get('purchase_ref') || params.get('ref') || params.get('reference') || params.get('transaction_ref') || '',
    returnStatus: params.get('status') || '',
    isFreeFlow: freeParam === '1' || freeParam === 'true' || freeParam === 'yes',
  };
};

const terminalStatuses = new Set(['paid', 'completed', 'complete', 'success', 'successful', 'failed', 'cancelled', 'canceled', 'expired', 'refunded']);

const isTerminalPaymentStatus = (paymentStatus) => terminalStatuses.has(String(paymentStatus || '').toLowerCase());

const getStatusSeverity = (paymentStatus, fallbackStatus) => {
  const normalizedStatus = String(paymentStatus || fallbackStatus || '').toLowerCase();
  if (['paid', 'completed', 'complete', 'success', 'successful'].includes(normalizedStatus)) return 'success';
  if (['failed', 'cancelled', 'canceled', 'expired'].includes(normalizedStatus)) return 'error';
  return 'info';
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const parsePaymentDateTime = (dateValue, timeValue) => {
  if (!dateValue) return null;
  const hasDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateValue);
  const value = timeValue && hasDateOnly ? `${dateValue}T${timeValue}` : dateValue;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getPaymentEventDateRange = (timings) => {
  const startDate = parsePaymentDateTime(timings?.event_start_date, timings?.event_start_time)
    || parsePaymentDateTime(timings?.session_date, timings?.session_time);
  if (!startDate) return { startDate: null, endDate: null };

  return {
    startDate,
    endDate: parsePaymentDateTime(timings?.event_end_date, timings?.event_end_time) || new Date(startDate.getTime() + 60 * 60 * 1000),
  };
};

const formatCalendarDate = (date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

const escapeCalendarText = (value) => String(value || '').replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n');

const buildCalendarHref = ({ ticketDetails, timings, transactionReference }) => {
  const { startDate, endDate } = getPaymentEventDateRange(timings);
  if (!startDate) return '';

  const title = ticketDetails?.event_title || ticketDetails?.session_title || 'Registered Event';
  const description = [
    ticketDetails?.ticket_type_label ? `Ticket: ${ticketDetails.ticket_type_label}` : '',
    ticketDetails?.quantity ? `Quantity: ${ticketDetails.quantity}` : '',
    transactionReference ? `Transaction: ${transactionReference}` : '',
  ].filter(Boolean).join('\\n');

  const calendarContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Sidra Medicine//Events//EN',
    'BEGIN:VEVENT',
    `UID:${escapeCalendarText(transactionReference || `${title}-${startDate.toISOString()}`)}@sidra-events`,
    `DTSTAMP:${formatCalendarDate(new Date())}`,
    `DTSTART:${formatCalendarDate(startDate)}`,
    `DTEND:${formatCalendarDate(endDate)}`,
    `SUMMARY:${escapeCalendarText(title)}`,
    `DESCRIPTION:${escapeCalendarText(description)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return `data:text/calendar;charset=utf-8,${encodeURIComponent(calendarContent)}`;
};

const DetailRow = ({ label, value }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 2 }} justifyContent="space-between" sx={paymentStatusStyles.detailRow}>
    <Typography variant="body2" color="text.secondary" sx={paymentStatusStyles.detailLabel}>{label}</Typography>
    <Typography variant="body2" sx={paymentStatusStyles.detailValue}>{value || '-'}</Typography>
  </Stack>
);

const getAttendees = (paymentDetails) => {
  if (Array.isArray(paymentDetails?.attendees) && paymentDetails.attendees.length) return paymentDetails.attendees;
  if (paymentDetails?.attendee) return [paymentDetails.attendee];
  return [];
};

const buildRetryRegistrationPath = (paymentDetails, fallbackRef) => {
  const attendees = getAttendees(paymentDetails);
  const primaryAttendee = attendees[0];
  const eventSlug = primaryAttendee?.event_slug;
  const sessionId = primaryAttendee?.session_id;
  const ticketTypeId = primaryAttendee?.ticket_type_id;
  const purchaseRef = primaryAttendee?.purchase_ref || paymentDetails?.purchase_ref || paymentDetails?.reference || fallbackRef;

  if (!eventSlug || !sessionId || !ticketTypeId || !purchaseRef) return '';

  const nextParams = new URLSearchParams({
    session: String(sessionId),
    ticket: `ticket-${ticketTypeId}`,
    retry: '1',
    ref: String(purchaseRef),
  });

  return `/events/${encodeURIComponent(eventSlug)}?${nextParams.toString()}`;
};

const statusConfig = {
  complete: {
    title: 'Payment Status',
    pending: 'Confirming your payment...',
    success: 'Your registration payment is confirmed.',
    failure: 'We could not retrieve the payment status.',
    mark: '✓',
  },
  cancel: {
    title: 'Payment Cancelled',
    pending: 'Recording the cancelled payment...',
    success: 'Your payment cancellation has been recorded.',
    failure: 'We could not record the cancellation automatically.',
    mark: '!',
  },
};

const PaymentStatusPage = ({ status }) => {
  const location = useLocation();
  const [statusState, setStatusState] = useState({ loading: true, error: '', response: null, attempts: 0 });
  const [attendeeEdits, setAttendeeEdits] = useState({});
  const [attendeeUpdateState, setAttendeeUpdateState] = useState({});
  const config = statusConfig[status] || statusConfig.complete;
  const { purchaseRef, returnStatus, isFreeFlow } = useMemo(() => getPaymentReturnParams(location), [location]);
  const isFreeCompletionWithoutReference = status === 'complete' && isFreeFlow && !purchaseRef;

  useEffect(() => {
    if (!purchaseRef) {
      return undefined;
    }

    let ignore = false;
    let timeoutId;
    const maxAttempts = 10;

    const pollPaymentStatus = async (attempt = 1) => {
      setStatusState((currentState) => ({ ...currentState, loading: true, error: '', attempts: attempt }));
      try {
        const response = await fetchPaymentStatus(purchaseRef);
        if (ignore) return;

        const paymentStatus = response.data?.payment_status || response.data?.status;
        const shouldContinuePolling = attempt < maxAttempts && !isTerminalPaymentStatus(paymentStatus);
        setStatusState({ loading: shouldContinuePolling, error: '', response: response.data, attempts: attempt });

        if (shouldContinuePolling) {
          timeoutId = window.setTimeout(() => pollPaymentStatus(attempt + 1), 3000);
        }
      } catch (error) {
        const message = error.response?.data?.detail || error.response?.data?.message || error.message;
        if (!ignore) setStatusState({ loading: false, error: message, response: null, attempts: attempt });
      }
    };

    pollPaymentStatus();

    return () => {
      ignore = true;
      window.clearTimeout(timeoutId);
    };
  }, [purchaseRef]);

  const missingReferenceError = isFreeCompletionWithoutReference || purchaseRef ? '' : 'Missing purchase reference in payment return URL.';
  const displayError = missingReferenceError || statusState.error;
  const displayLoading = Boolean(purchaseRef && statusState.loading);
  const paymentDetails = statusState.response;
  const attendees = useMemo(() => getAttendees(paymentDetails), [paymentDetails]);
  const isMultiAttendee = attendees.length > 1;
  const severity = isFreeCompletionWithoutReference
    ? 'success'
    : displayError
      ? 'error'
      : getStatusSeverity(paymentDetails?.payment_status || paymentDetails?.status, returnStatus);
  const isSuccessful = severity === 'success';
  const isFailed = severity === 'error';
  const allowAttendeeUpdates = status === 'complete' && isSuccessful && isMultiAttendee;
  const paymentFailureMessage = paymentDetails?.failure_message || paymentDetails?.error_message || paymentDetails?.message || '';
  const responseAmount = Number(paymentDetails?.amount);
  const ticketTotalAmount = Number(paymentDetails?.ticket_details?.total_amount);
  const inferredFreeFromResponse = Number.isFinite(responseAmount)
    ? responseAmount <= 0
    : Number.isFinite(ticketTotalAmount) && ticketTotalAmount <= 0;
  const isFreeRegistration = Boolean(status === 'complete' && (isFreeFlow || isFreeCompletionWithoutReference || inferredFreeFromResponse));
  const statusLabel = missingReferenceError
    ? 'Missing reference'
    : paymentDetails?.payment_status || paymentDetails?.status || returnStatus || (isFreeCompletionWithoutReference ? 'Complete' : 'Checking');
  const amountDisplay = paymentDetails?.amount ? `${paymentDetails.amount} ${paymentDetails.currency || 'QAR'}` : '-';
  const transactionReference = paymentDetails?.transaction_reference || paymentDetails?.reference || paymentDetails?.purchase_ref || purchaseRef;
  const ticketDetails = paymentDetails?.ticket_details;
  const timings = paymentDetails?.timings;
  const eventDateRange = getPaymentEventDateRange(timings);
  const eventTimingDisplay = eventDateRange.startDate ? `${formatDateTime(eventDateRange.startDate)}${eventDateRange.endDate ? ` - ${formatDateTime(eventDateRange.endDate)}` : ''}` : '-';
  const ticketTotal = ticketDetails?.total_amount ? `${ticketDetails.total_amount} ${paymentDetails?.currency || 'QAR'}` : amountDisplay;
  const unitPrice = ticketDetails?.unit_price ? `${ticketDetails.unit_price} ${paymentDetails?.currency || 'QAR'}` : '-';
  const receiptTransactionRef = paymentDetails?.transaction_reference;
  const receiptUrl = receiptTransactionRef ? `/events/download/${encodeURIComponent(receiptTransactionRef)}?type=receipt` : '';
  const ticketUrl = receiptTransactionRef ? `/events/download/${encodeURIComponent(receiptTransactionRef)}?type=ticket` : '';
  const calendarHref = ticketDetails ? buildCalendarHref({ ticketDetails, timings, transactionReference }) : '';
  const retryRegistrationUrl = isFailed ? buildRetryRegistrationPath(paymentDetails, purchaseRef) : '';
  const outcomeTitle = displayLoading
    ? 'Checking Payment Status'
    : isFreeRegistration
      ? 'Registration Confirmed'
      : isSuccessful
        ? 'Registration Payment Confirmed'
        : isFailed
          ? 'Payment Failed'
          : 'Payment Status Updated';
  const outcomeMessage = displayLoading
    ? 'Please wait while we retrieve the latest transaction status.'
    : isFreeRegistration
      ? 'Your free registration has been completed successfully.'
    : displayError
      ? displayError
      : isSuccessful
        ? config.success
        : isFailed
          ? paymentFailureMessage || 'The transaction was not completed. No confirmed payment was found for this reference.'
          : 'The latest transaction status is shown below.';
  const outcomeStyle = displayLoading ? paymentOutcomeStyles.loading : isSuccessful ? paymentOutcomeStyles.success : isFailed ? paymentOutcomeStyles.error : paymentOutcomeStyles.info;

  useEffect(() => {
    if (!attendees.length) {
      setAttendeeEdits({});
      setAttendeeUpdateState({});
      return;
    }

    setAttendeeEdits((previousEdits) => {
      const nextEdits = { ...previousEdits };
      attendees.forEach((attendee) => {
        const attendeeKey = String(attendee.id || '');
        if (!attendeeKey) return;
        nextEdits[attendeeKey] = {
          name: attendee.name || '',
          email: attendee.email || '',
          phone: attendee.phone || '',
        };
      });
      return nextEdits;
    });
  }, [attendees]);

  const handleAttendeeFieldChange = (attendeeId, field) => (event) => {
    const attendeeKey = String(attendeeId);
    setAttendeeEdits((previousEdits) => ({
      ...previousEdits,
      [attendeeKey]: {
        ...previousEdits[attendeeKey],
        [field]: event.target.value,
      },
    }));

    setAttendeeUpdateState((previousState) => ({
      ...previousState,
      [attendeeKey]: {
        ...previousState[attendeeKey],
        error: '',
        success: '',
      },
    }));
  };

  const handleAttendeeUpdate = async (attendee) => {
    const attendeeKey = String(attendee.id || '');
    const formValues = attendeeEdits[attendeeKey] || {};
    const name = (formValues.name || '').trim();
    const email = (formValues.email || '').trim();
    const phone = (formValues.phone || '').trim();

    if (!name && !email && !phone) {
      setAttendeeUpdateState((previousState) => ({
        ...previousState,
        [attendeeKey]: { ...(previousState[attendeeKey] || {}), saving: false, success: '', error: 'At least one of name, email, or phone is required.' },
      }));
      return;
    }

    const payload = {
      attendee_id: attendee.id,
      purchase_ref: paymentDetails?.purchase_ref || paymentDetails?.reference || purchaseRef,
      ...(name ? { name } : {}),
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
    };

    setAttendeeUpdateState((previousState) => ({
      ...previousState,
      [attendeeKey]: { ...(previousState[attendeeKey] || {}), saving: true, success: '', error: '' },
    }));

    try {
      await updateAttendeeInfo(payload);

      setStatusState((currentState) => {
        if (!currentState.response) return currentState;

        const currentAttendees = getAttendees(currentState.response);
        const nextAttendees = currentAttendees.map((item) => (String(item.id) === attendeeKey ? { ...item, ...payload } : item));
        const hasAttendeesArray = Array.isArray(currentState.response.attendees);

        return {
          ...currentState,
          response: {
            ...currentState.response,
            ...(hasAttendeesArray ? { attendees: nextAttendees } : {}),
            ...(currentState.response.attendee && String(currentState.response.attendee.id) === attendeeKey
              ? { attendee: { ...currentState.response.attendee, ...payload } }
              : {}),
          },
        };
      });

      setAttendeeUpdateState((previousState) => ({
        ...previousState,
        [attendeeKey]: { ...(previousState[attendeeKey] || {}), saving: false, error: '', success: 'Attendee information updated.' },
      }));
    } catch (error) {
      const message = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unable to update attendee information.';
      setAttendeeUpdateState((previousState) => ({
        ...previousState,
        [attendeeKey]: { ...(previousState[attendeeKey] || {}), saving: false, success: '', error: message },
      }));
    }
  };

  return (
    <Box sx={paymentStatusStyles.page}>
      <Box sx={topBarStyles.shell}>
        <EventTopBar />
      </Box>

      <Container maxWidth="md" sx={paymentStatusStyles.container}>
        <Card sx={paymentStatusStyles.card}>
          <CardContent sx={paymentStatusStyles.cardContent}>
            <Stack spacing={3} sx={paymentStatusStyles.contentStack}>
              <Box sx={paymentStatusStyles.iconWrap(outcomeStyle)}>
                {displayLoading ? (
                  <CircularProgress size={54} />
                ) : (
                  <Box sx={paymentStatusStyles.statusMark}>
                    {isSuccessful ? '✓' : isFailed ? '!' : config.mark}
                  </Box>
                )}
              </Box>

              <Box sx={paymentStatusStyles.headline}>
                <Typography variant="h4" sx={paymentStatusStyles.title(outcomeStyle)}>{outcomeTitle}</Typography>
                <Typography color="text.secondary">
                  {outcomeMessage}
                </Typography>
              </Box>

              {purchaseRef && !displayError && !isFreeRegistration && (
                <Stack spacing={0.75} alignItems="center" sx={paymentStatusStyles.amountPanel(outcomeStyle)}>
                  <Typography variant="caption" color="text.secondary" sx={paymentStatusStyles.amountLabel}>Amount</Typography>
                  <Typography variant="h4" sx={paymentStatusStyles.amount(outcomeStyle)}>{amountDisplay}</Typography>
                  <Typography variant="body2" color="text.secondary">{`Status: ${statusLabel}`}</Typography>
                </Stack>
              )}

              {displayError && (
                <Alert severity="error" sx={paymentStatusStyles.errorAlert}>{displayError}</Alert>
              )}

              {!displayError && isFailed && paymentFailureMessage && (
                <Alert severity="error" sx={paymentStatusStyles.errorAlert}>{paymentFailureMessage}</Alert>
              )}

              {purchaseRef && (
                <Box sx={paymentStatusStyles.detailsPanel}>
                  <Stack spacing={0.5} alignItems="center" sx={paymentStatusStyles.detailsHeader}>
                    <Typography variant="subtitle1" sx={paymentStatusStyles.detailsTitle}>{isFreeRegistration ? 'Registration Details' : 'Transaction Details'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isFreeRegistration ? 'Latest details from the registration record.' : 'Latest details from the payment record.'}
                    </Typography>
                  </Stack>
                  <Divider />
                  <DetailRow label={isFreeRegistration ? 'Registration Reference' : 'Transaction Reference'} value={transactionReference} />
                  {!isFreeRegistration && <DetailRow label="Paid At" value={formatDateTime(paymentDetails?.paid_at)} />}
                  <DetailRow label="Created At" value={formatDateTime(paymentDetails?.created_at)} />
                </Box>
              )}

              {isSuccessful && ticketDetails && (
                <Box sx={paymentStatusStyles.ticketPanel}>
                  <Stack spacing={0.5} alignItems="center" sx={paymentStatusStyles.detailsHeader}>
                    <Typography variant="subtitle1" sx={paymentStatusStyles.detailsTitle}>{isFreeRegistration ? 'Registered Ticket' : 'Purchased Ticket'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isFreeRegistration ? 'Registration details for this ticket.' : 'Registration details for this purchase.'}
                    </Typography>
                  </Stack>
                  <Divider />
                  <DetailRow label="Event" value={ticketDetails.event_title} />
                  <DetailRow label="Occurrence" value={ticketDetails.session_title} />
                  <DetailRow label="Timing" value={eventTimingDisplay} />
                  <DetailRow label="Ticket" value={ticketDetails.ticket_type_label} />
                  <DetailRow label="Quantity" value={ticketDetails.quantity} />
                  {isFreeRegistration
                    ? <DetailRow label="Cost" value="Free" />
                    : <>
                        <DetailRow label="Unit Price" value={unitPrice} />
                        <DetailRow label="Total" value={ticketTotal} />
                      </>}
                </Box>
              )}

              {status === 'complete' && attendees.length > 0 && (
                <Box sx={paymentStatusStyles.ticketPanel}>
                  {isMultiAttendee ? (
                    <>
                      <Stack spacing={0.5} alignItems="center" sx={paymentStatusStyles.detailsHeader}>
                        <Typography variant="subtitle1" sx={paymentStatusStyles.detailsTitle}>Attendees</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Review and update attendee information for this purchase.
                        </Typography>
                      </Stack>
                      <Divider />
                      <Stack spacing={2} sx={{ mt: 2 }}>
                        {attendees.map((attendee, index) => {
                          const attendeeKey = String(attendee.id || `attendee-${index}`);
                          const formValues = attendeeEdits[attendeeKey] || {
                            name: attendee.name || '',
                            email: attendee.email || '',
                            phone: attendee.phone || '',
                          };
                          const updateState = attendeeUpdateState[attendeeKey] || {};

                          return (
                            <Box key={attendeeKey} sx={{ border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 2, p: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{`Attendee ${index + 1}`}</Typography>
                              {allowAttendeeUpdates ? (
                                <Stack spacing={1.5}>
                                  <TextField
                                    label="Name"
                                    size="small"
                                    value={formValues.name}
                                    onChange={handleAttendeeFieldChange(attendee.id, 'name')}
                                    disabled={updateState.saving}
                                    fullWidth
                                  />
                                  <TextField
                                    label="Email"
                                    size="small"
                                    type="email"
                                    value={formValues.email}
                                    onChange={handleAttendeeFieldChange(attendee.id, 'email')}
                                    disabled={updateState.saving}
                                    fullWidth
                                  />
                                  <TextField
                                    label="Phone"
                                    size="small"
                                    value={formValues.phone}
                                    onChange={handleAttendeeFieldChange(attendee.id, 'phone')}
                                    disabled={updateState.saving}
                                    fullWidth
                                  />
                                  {updateState.error && <Alert severity="error">{updateState.error}</Alert>}
                                  {updateState.success && <Alert severity="success">{updateState.success}</Alert>}
                                  <Stack direction="row" justifyContent="flex-end">
                                    <Button variant="contained" onClick={() => handleAttendeeUpdate(attendee)} disabled={updateState.saving}>
                                      {updateState.saving ? 'Updating...' : 'Update Attendee'}
                                    </Button>
                                  </Stack>
                                </Stack>
                              ) : (
                                <Stack spacing={0.25}>
                                  <DetailRow label="Name" value={attendee.name} />
                                  <DetailRow label="Email" value={attendee.email} />
                                  <DetailRow label="Phone" value={attendee.phone} />
                                </Stack>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    </>
                  ) : (
                    <Stack spacing={0.25}>
                      <DetailRow label="Name" value={attendees[0]?.name} />
                      <DetailRow label="Email" value={attendees[0]?.email} />
                      <DetailRow label="Phone" value={attendees[0]?.phone} />
                    </Stack>
                  )}
                </Box>
              )}

              {isSuccessful && (receiptUrl || ticketUrl || ticketDetails) && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={paymentStatusStyles.paymentActions}>
                  
                  {ticketUrl && (
                    <Button component={RouterLink} target="_blank" to={ticketUrl} variant="outlined" sx={paymentStatusStyles.secondaryActionButton}>
                      Download Ticket
                    </Button>
                  )}
                  {receiptUrl && (
                    <Button component={RouterLink} target="_blank" to={receiptUrl} variant="contained" sx={paymentStatusStyles.primaryActionButton}>
                      Download Receipt
                    </Button>
                  )}
                  <Tooltip title={calendarHref ? '' : 'Calendar date is not available for this ticket yet'}>
                    <span>
                      <Button component="a" href={calendarHref || undefined} variant="outlined" sx={paymentStatusStyles.secondaryActionButton} download="event.ics" disabled={!calendarHref}>
                        Add to Calendar
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={paymentStatusStyles.paymentActions}>
                {retryRegistrationUrl && (
                  <Button component={RouterLink} to={retryRegistrationUrl} variant="contained" sx={paymentStatusStyles.primaryActionButton}>
                    Retry Payment
                  </Button>
                )}
                <Button component={RouterLink} to="/" variant="text" size="small" sx={paymentStatusStyles.secondaryActionButton}>Browse More Events</Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default PaymentStatusPage;
